import type {ReactElement} from 'react';
import React, {useEffect, useRef, useState} from 'react';
import {Icon} from '@Components/icon-v2';
import {FlatIconColor, FlatIconColorType, IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import styles from '@Components/download-panel/download-panel.module.scss';
import {DownloadStateTypes, updateDisplayProgress} from '@Components/download-panel/download-panel.reducer';
import {DownloadPanelUpselling} from '@Components/download-panel/components/download-panel-upselling/download-panel-upselling';
import {useAppDispatch, useAppSelector} from '@/hooks';
import type {FileTypeOptions} from '@Components/download-panel/components/file-type-option/file-type-option.types';
import {noop} from '@Utils/general.util';
import {GA4EventName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import {CircularProgressLoader, LOADER_COLOR, LOADER_SIZE} from '@Components/circular-progress-loader';
import {Button, Size, Type} from '@Components/button';

interface DownloadPanelDownloadingSectionProps {
  name: string;
  fileType: FileTypeOptions;
  onClickBack?: () => void;
  isOpenedInModal?: boolean;
  isOpenedInEditor?: boolean;
  isOpenedInPrintShop?: boolean;
  onSaveToDropbox?: () => Promise<void>;
  isDropboxDownload?: boolean;
  hasClosedUpsellingInThisSession?: boolean;
  onCloseUpselling?: () => void;
  isOpenedInLoadingPopup?: boolean;
}

export function DownloadPanelDownloadingSection({
  isOpenedInModal = false,
  isOpenedInPrintShop = false,
  isOpenedInEditor = true,
  hasClosedUpsellingInThisSession = false,
  onCloseUpselling = noop,
  isOpenedInLoadingPopup = false,
  isDropboxDownload = false,
  ...props
}: DownloadPanelDownloadingSectionProps): ReactElement {
  const [isComplete, setIsComplete] = useState(false);
  const [isError, setIsError] = useState(false);
  const animationFrameId = useRef<number | null>(null);
  const orderHashmap = useAppSelector((state) => {
    return state.downloadDialog.orderStateHashmap;
  });
  const currentDownloadOrderId = useAppSelector((state) => {
    return state.downloadDialog.currentProcessingOrderId;
  });
  const displayProgress = useAppSelector((state) => {
    return state.downloadDialog.orderStateHashmap[state.downloadDialog.currentProcessingOrderId ?? '']?.displayProgress ?? 0;
  });
  const dispatch = useAppDispatch();
  const wasInLoadingStateBefore = useRef(false);

  const setDisplayProgress = (value: number): void => {
    dispatch(updateDisplayProgress({orderId: currentDownloadOrderId ?? '', displayProgress: value}));
  };

  useEffect(() => {
    if (!currentDownloadOrderId) {
      return;
    }
    if (orderHashmap[currentDownloadOrderId].state === DownloadStateTypes.READY) {
      setDisplayProgress(100);
      setIsComplete(true);
      return;
    }

    const duration1 = 90000;
    const startValue1 = 0;
    const endValue1 = 90;

    const duration2 = 405000;
    const startValue2 = 90;
    const endValue2 = 99;

    const totalDuration = duration1 + duration2;
    let startTime: number | null = null;

    const animate = (timestamp: number): void => {
      startTime ??= timestamp;
      const elapsed = timestamp - startTime;
      let newProgress = 0;

      if (elapsed < duration1) {
        const progressFraction = elapsed / duration1;
        newProgress = startValue1 + (endValue1 - startValue1) * progressFraction;
      } else if (elapsed < totalDuration) {
        const elapsedInPhase2 = elapsed - duration1;
        const progressFraction = elapsedInPhase2 / duration2;
        newProgress = startValue2 + (endValue2 - startValue2) * progressFraction;
      } else {
        newProgress = 99;
      }

      setDisplayProgress(newProgress);

      if (elapsed < totalDuration) {
        animationFrameId.current = requestAnimationFrame(animate);
      }
    };
    wasInLoadingStateBefore.current = true;
    animationFrameId.current = requestAnimationFrame(animate);

    return (): void => {
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!currentDownloadOrderId) {
      return;
    }
    if (orderHashmap[currentDownloadOrderId].state === DownloadStateTypes.READY) {
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }
      if (!wasInLoadingStateBefore.current) {
        return;
      }
      const startValue = displayProgress;
      const endValue = 100;
      const duration = 400;
      let startTime: number | null = null;

      const animateToCompletion = (timestamp: number): void => {
        startTime ??= timestamp;
        const elapsed = timestamp - startTime;
        const progressFraction = Math.min(elapsed / duration, 1);
        const newProgress = startValue + (endValue - startValue) * progressFraction;

        setDisplayProgress(newProgress);

        if (progressFraction < 1) {
          animationFrameId.current = requestAnimationFrame(animateToCompletion);
        } else {
          setIsComplete(true);
        }
      };

      animationFrameId.current = requestAnimationFrame(animateToCompletion);
    } else if (orderHashmap[currentDownloadOrderId].state === DownloadStateTypes.ERROR) {
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }
      setDisplayProgress(100);
      setIsError(true);
    }
  }, [orderHashmap[currentDownloadOrderId ?? '']?.state]);

  const getExtendedOption = (): ReactElement | null => {
    if (!isReady()) {
      return null;
    }

    const printShopLink = orderHashmap[currentDownloadOrderId ?? '']?.printShopLink;

    if (!printShopLink || isOpenedInPrintShop) {
      return null;
    }

    return (
      <div className="spacing-m-t-2 flex-h-row flex-items-center flex-justify-end">
        <Icon icon={'icon-print'} size={IconSize.SIZE_ICON_16} className={`content-primary ${styles.extendedOptionIcon}`} />
        <Text
          onClick={(): void => {
            trackPosterBuilderGA4Events(GA4EventName.PRINT_SHOP_LINK);
            window.open(window.PMW.util.site_url(`cart/downloadOptions/${printShopLink}`), '_blank');
          }}
          val={window.i18next.t('pmwjs_share_with_print_shop')}
          bold
          size={TextSize.XSMALL}
          className="content-primary _cursor-pointer"
          underline
        />
      </div>
    );
  };

  const getIconNameForState = (): string => {
    if (isComplete) {
      return 'icon-check';
    }

    if (isError) {
      return 'icon-exclamation-triangle';
    }

    return 'icon-cloud-download';
  };

  const showLoadingForDropbox = (): ReactElement => {
    return (
      <div className="spacing-m-t-2 flex flex-v-row">
        <div className="flex flex-h-row flex-items-center">
          {isComplete ? (
            <Icon
              icon="icon-designs"
              className="spacing-m-r-4"
              size={IconSize.SIZE_ICON_20}
              shape={IconShape.CIRCLE}
              type={IconType.FLAT}
              flatIconType={{
                color: FlatIconColor.NEUTRAL,
                type: FlatIconColorType.DEFAULT,
              }}
            />
          ) : (
            <CircularProgressLoader classes="spacing-m-r-2 spacing-m-l-0" color={LOADER_COLOR.PRIMARY_DARK} size={LOADER_SIZE.SMALL} />
          )}
          {isComplete ? (
            <Text val={`${props.name}.${props.fileType}`} size={TextSize.XSMALL} className="content-body" />
          ) : (
            <Text
              className="content-body"
              dangerouslySetInnerHTML
              size={TextSize.XSMALL}
              val={window.i18next.t('pmwjs_preparing_design', {
                name: `${props.name}.${props.fileType}`,
              })}
            />
          )}
        </div>
        <Button
          size={Size.SMALL}
          type={Type.PRIMARY}
          disabled={!isComplete}
          text={window.i18next.t('pmwjs_save_file')}
          customClasses="spacing-m-t-6"
          onClick={(): void => {
            if (props.onSaveToDropbox) {
              void props.onSaveToDropbox();
            }
          }}
        />
      </div>
    );
  };

  const getLoading = (): ReactElement => {
    if (isDropboxDownload) {
      return showLoadingForDropbox();
    }
    const roundedProgress = Math.round(displayProgress);
    return (
      <div className="flex flex-h-row flex-items-center content-body">
        <div className={`radius-round flex-center ${styles.iconOuterContainer} ${isError ? styles.error : ''} ${isComplete ? styles.success : ''} spacing-m-r-3`}>
          <div className={`${styles.iconInnerContainer} ${isError ? styles.error : ''} ${isComplete ? styles.success : ''} radius-round flex-center`}>
            {isError || isComplete ? (
              <Icon icon={getIconNameForState()} size={IconSize.SIZE_ICON_20} className={isError ? 'content-danger' : 'content-primary'} />
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path
                  className={styles.arrowHead}
                  d="M6.07637 13.3124C6.31324 13.0755 6.69699 13.0755 6.93324 13.3124L9.73699 16.1162L12.5407 13.3124C12.7776 13.0755 13.1614 13.0755 13.3976 13.3124C13.6339 13.5493 13.6345 13.933 13.3976 14.1693L10.1651 17.4018C10.0514 17.5155 9.89762 17.5793 9.73637 17.5793C9.57512 17.5793 9.42137 17.5155 9.30762 17.4018L6.07512 14.1693C5.83824 13.9324 5.83824 13.5487 6.07512 13.3124H6.07637Z"
                  fill="#00688C"
                />
                <path
                  className={styles.arrowLine}
                  d="M9.73711 9.09375C10.0721 9.09375 10.3434 9.365 10.3434 9.7V16.9725C10.3434 17.3069 10.0721 17.5788 9.73711 17.5788C9.40211 17.5788 9.13086 17.3075 9.13086 16.9725V9.7C9.13086 9.365 9.40211 9.09375 9.73711 9.09375Z"
                  fill="#00688C"
                />
                <path
                  d="M7.05078 1.81518C8.11953 1.77643 9.18328 1.98143 10.1614 2.4133C11.1395 2.84518 12.0077 3.49393 12.6989 4.30955C13.2877 5.00455 13.7364 5.8058 14.0202 6.6683H14.5839L7.05078 1.81518Z"
                  fill="#00688C"
                />
                <path
                  d="M7.03609 1.82121C8.10422 1.78183 9.16734 1.98496 10.1455 2.41558C11.1236 2.84621 11.9917 3.49308 12.6836 4.30746C13.2736 5.00121 13.7223 5.80121 14.008 6.66308H14.573C15.6055 6.66371 16.6086 7.00808 17.423 7.64183C18.2373 8.27558 18.8186 9.16308 19.073 10.1631C19.3273 11.1631 19.2417 12.22 18.8292 13.1668C18.4167 14.1131 17.7005 14.895 16.7942 15.3893C16.5005 15.5493 16.1323 15.4412 15.9717 15.1475C15.8111 14.8537 15.9198 14.4856 16.2136 14.325C16.8836 13.96 17.413 13.3818 17.718 12.6825C18.023 11.9831 18.0861 11.2018 17.898 10.4625C17.7098 9.72308 17.2805 9.06746 16.6786 8.59871C16.0767 8.12996 15.3355 7.87558 14.5723 7.87496L7.03609 1.82121ZM14.5736 7.87496H13.5555C13.2792 7.87496 13.038 7.68808 12.9686 7.42058C12.7467 6.56308 12.3336 5.76746 11.7605 5.09246C11.1873 4.41746 10.468 3.88183 9.65734 3.52496C8.84672 3.16808 7.96609 2.99996 7.08109 3.03246C6.19609 3.06496 5.32984 3.29808 4.54797 3.71308C3.76609 4.12808 3.08797 4.71621 2.56609 5.43121C2.04359 6.14621 1.69047 6.97058 1.53234 7.84183C1.37422 8.71308 1.41609 9.60933 1.65422 10.4618C1.89234 11.3143 2.32047 12.1025 2.90734 12.7662C3.12922 13.0168 3.10547 13.4 2.85422 13.6218C2.60297 13.8437 2.22047 13.82 1.99859 13.5693C1.29109 12.7687 0.77359 11.8175 0.486715 10.7881C0.19984 9.75871 0.149215 8.67746 0.33984 7.62558C0.530465 6.57371 0.95734 5.57933 1.58734 4.71621C2.21734 3.85308 3.03547 3.14433 3.97922 2.64308C4.92297 2.14183 5.96859 1.86058 7.03609 1.82121L14.5736 7.87496Z"
                  fill="#00688C"
                />
              </svg>
            )}
          </div>
        </div>
        <div className="flex-v-row flex-1">
          <div className="flex-h-row spacing-m-b-2">
            <Text className="flex-1" val={`${props.name}.${props.fileType}`} size={TextSize.XSMALL} bold />
            <Text val={`${roundedProgress}%`} size={TextSize.XXSMALL} bold className={isError ? '_hidden' : isComplete ? 'content-success' : 'content-primary'} />
          </div>
          <div className={styles.progressBarContainer}>
            <span className={`${styles.progressBarLoader} ${isError ? styles.error : ''} ${isComplete ? styles.success : ''}`} style={{width: `${displayProgress}%`}} />
          </div>
          {getExtendedOption()}
        </div>
      </div>
    );
  };

  const getUpsellingForModal = (): ReactElement | null => {
    if (!isOpenedInEditor) {
      return null;
    }
    if (!isOpenedInModal) {
      return null;
    }

    if (hasClosedUpsellingInThisSession) {
      return null;
    }

    return (
      <div className={`spacing-m-t-2 w-full h-full`}>
        <DownloadPanelUpselling onClose={onCloseUpselling} key={currentDownloadOrderId} isOpenedInModal />
      </div>
    );
  };

  const isReady = (): boolean => {
    return !!currentDownloadOrderId && orderHashmap[currentDownloadOrderId]?.state === DownloadStateTypes.READY;
  };

  const getHeadingForState = (): string => {
    if (isDropboxDownload) {
      return window.i18next.t('pmwjs_save_to_dropbox');
    }
    if (isComplete) {
      return window.i18next.t('pmwjs_enjoy_the_download');
    }

    if (isError) {
      return window.i18next.t('pmwjs_something_went_wrong');
    }

    return window.i18next.t('pmwjs_downloading');
  };

  const getDownloadingScreen = (): ReactElement => {
    return (
      <div className={`${isOpenedInLoadingPopup ? 'spacing-p-0' : 'spacing-p-4'} flex flex-v-row`}>
        {isOpenedInModal || isOpenedInLoadingPopup ? null : (
          <div className="flex flex-h-row flex-items-center spacing-m-b-4">
            {isComplete || isError ? (
              <Icon
                onClick={(): void => {
                  if (props.onClickBack) {
                    props.onClickBack();
                  }
                }}
                className="spacing-m-r-1"
                icon="icon-back"
                size={IconSize.SIZE_ICON_16}
                shape={IconShape.SQUARE}
                type={IconType.GHOST}
              />
            ) : null}
            <Text className="content-body" size={TextSize.SMALL} bold val={getHeadingForState()} />
          </div>
        )}
        {getLoading()}
        {getUpsellingForModal()}
      </div>
    );
  };

  return getDownloadingScreen();
}
